import { format, parseISO } from 'date-fns';
import feedbackType from '@/helpers/feedbackType';

import ValidRetry from '@/helpers/validRetry';
import { verifyContentOfEditorIsEmpty } from '~/helpers/stringHelper';

const DEFAULT_LOCALE_DATE_FORMAT = 'dd/MM/yyyy - HH:mm';

export default {
  computed: {
    sumPoints() {
      return this.content.totalGrade;
    }
  },
  methods: {
    isQuizExpired(content) {
      return !content.config.beforeDeadline;
    },
    isQuizFinished(content) {
      return content.config.allAttemptsCompleted;
    },
    hasRetries(content) {
      return content.hasRetries;
    },
    isStudentFirstAttempt(content) {
      return content.attempts.length > 0 && content.attempts[content.attempts.length - 1].finishedAt;
    },
    shouldShowDeadline(content) {
      return content.hasDeadline && content.deadlineAt !== null;
    },
    shouldShowFeedback(content) {
      return (this.isQuizFinished(content) || this.isQuizExpired(content)) && content.config.feedbackReleased;
    },
    hasFeedbackByOption(question) {
      return question.feedbackTypeId === feedbackType.BY_OPTION && question.options[0] && question.options[0].feedback && question.options[0].feedback.length > 0;
    },
    hasFeedbackByQuestion(question) {
      return question.feedbackTypeId === feedbackType.GENERAL && question.feedback && !verifyContentOfEditorIsEmpty(question.feedback);
    },
    hasStudentInitiatedAtLeastOneAttempt(content) {
      return content.attempts.length > 0;
    },
    hasStudentFinishedAtLeastOneAttempt(content) {
      const firstAttemptFinishDate = content.attempts[0] && content.attempts[0].finishedAt;

      return firstAttemptFinishDate && firstAttemptFinishDate !== content.deadlineAt;
    },
    hasStudentFinishedLastAttempt(attemptsArray, quizQuestions, numberRetries = 1, content = null) {
      if (attemptsArray.length === 0) {
        if (content && this.isQuizExpired(content)) {
          return false;
        }

        return true;
      } else if (attemptsArray.length > 0 && attemptsArray.length < numberRetries) {
        const lastAttempt = attemptsArray[attemptsArray.length - 1];

        return this.checkIfAllQuizQuestionsAreAnswered(lastAttempt, quizQuestions) && lastAttempt.finishedAt;
      }
    },
    isQuestionAnswerCorrect(questionId, attemptAnswers) {
      if (attemptAnswers) {
        return attemptAnswers.find((answer) => {
          return answer.questionId === questionId && answer.isCorrect;
        });
      }
    },
    isOptionSelectedByStudent(questionId, optionId, attempt = null) {
      const currentAttempt = attempt || this.findCurrentAttempt(this.content.attempts);

      if (currentAttempt && currentAttempt.answers.length > 0) {
        const isSelected = currentAttempt.answers.find((answer) => {
          return answer.questionId === questionId && answer.optionId === optionId;
        });

        return !!isSelected;
      }
    },
    isOptionCorrect(option) {
      return option.isCorrect;
    },
    findCurrentAttempt(attemptsArray = null) {
      const attempts = attemptsArray || this.content.attempts;
      return attempts[attempts.length - 1];
    },
    lastFinishedAttempt(attemptsArray = null) {
      for (let index = attemptsArray.length; index >= 0; index--) {
        if (attemptsArray[index - 1].finishedAt) {
          return attemptsArray[index - 1];
        }
      }
    },
    findQuestionInCurrentAttemptAnswers(questionId, answersArray) {
      return answersArray.find((answer) => answer.questionId === questionId);
    },
    studentCorrectAnswersCount(answers) {
      return answers.reduce((accumulator, current) => {
        if (current.isCorrect) {
          return accumulator + 1;
        }

        return accumulator;
      }, 0);
    },
    checkIfAllQuizQuestionsAreAnswered(attempt, quizQuestions) {
      return quizQuestions.every((question) => {
        const studentAttemptAnswer = this.findQuestionInCurrentAttemptAnswers(question.questionId, attempt.answers);

        return !!studentAttemptAnswer;
      });
    },
    getValidAttemptText(retryTypeId) {
      if (retryTypeId === ValidRetry.HIGHEST) {
        return this.$t('student.quiz.retries.validRetryTypeBiggestGrade');
      } else if (retryTypeId === ValidRetry.AVERAGE) {
        return this.$t('student.quiz.retries.validRetryTypeAverageGrade');
      } else if (retryTypeId === ValidRetry.LAST) {
        return this.$t('student.quiz.retries.validRetryTypeLastAttempt');
      }
    },
    isAverageAttempts(retryTypeId) {
      return retryTypeId === ValidRetry.AVERAGE;
    },
    isValidAttempt(enrollmentQuizAttemptId, attempt) {
      return enrollmentQuizAttemptId ? (enrollmentQuizAttemptId === attempt.attemptId) : false;
    },
    isHighestGradeAttempt(attempt, attemptsArray) {
      const highestGradeAttempt = attemptsArray.reduce((accumulator, current) => {
        return Number(accumulator.grade) > Number(current.grade) ? accumulator : current;
      });

      return attempt.attemptId === highestGradeAttempt.attemptId;
    },
    isLastAttemptValidAttempt(attempt, attemptsArray) {
      const lastAttempt = attemptsArray[attemptsArray.length - 1];

      return attempt.attemptId === lastAttempt.attemptId;
    },
    parseDate(date) {
      if (!date) {
        return '';
      }

      let parsedDate;

      if (typeof date === 'string' || date instanceof String) {
        parsedDate = parseISO(date);
      }

      return format(parsedDate, DEFAULT_LOCALE_DATE_FORMAT);
    }
  }
};
